//==========================================
//⚠️ Keep index files under 100 lines!
//Split logic into submodules as needed.
//==========================================
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@mui/material";
import { IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/system";
import { Breadcrumbs } from "@vds/breadcrumbs";
import { Button, TextLink } from "@vds/buttons";
import { Checkbox } from "@vds/checkboxes";
import { DatePicker } from "@vds/date-pickers";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody } from "@vds/modals";
import { Title } from "@vds/typography";
import { endOfDay } from "date-fns";
import moment from "moment";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useDispatch, useSelector } from "react-redux";
import { fetchLocationData, resolveBulkEvents } from "../../api/IncidentsManager";
import { useLocation } from "react-router-dom";
import FilterComponentUI from "../../global/components/Filters/filterComponent";
import EventTable from "../../global/components/Tables/EventTable";
import { getIncidentsData } from "../../global/utility/Utils";
import {
  addBulkSelectedIncidents,
  addLastUpdatedTime,
  eventPageNumber,
  eventSettings,
  fetchCVApp,
  removeBulkSelectedIncidents,
  showNotificationBanner,
  checkResolveStatusRequest,
  stopResolveStatusPolling
} from "../../store/actions";
import "../../styles/deviceStyle.css";
import FilterSpreadContainer from "./Components/FilterSpreadContainer";
import SelectAllEventsBanner from "./Components/SelectAllEventsBanner";
import { INCIDENTS_TEST_IDS } from "./incidentsConstants";
import "./index.scss";
import BulkResolveEventsModal from "./Modals/BulkResolveEventsModal";
import GettingStartedModal from "../Dashboard/GettingStartedModal";
import { getFormattedDate } from "../../utils";
import { FILTERS, SEVERITY_LEVELS, EVENT_STATUS, DATE_TIME, MAX_BULK_RESOLVE_LIMIT, EVENTS_INFO_CONSTANTS, RESOLVE_STATUS } from "../../global/constants/constants";

const Incidents = (props) => {
  const scrollPositionRef = useRef(0);
  const isInitialMount = useRef(true);
  const location = useLocation();
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const filterOptions = useSelector((state) => state.eventSettingsReducer);
  const { pageNumber: reducerPageNumber } = useSelector((state) => state.eventPageNumberReducer);
  const { incidentsList, selectedIncidentList } = useSelector((state) => state?.availableIncidentsReducer);
  const { resolveStatus } = useSelector((state) => state?.eventReducer);
  const [showResolveEvents, setShowResolveEvents] = useState({ isOpen: false });
  const [actionType, setActionType] = useState("selectedids");
  const [isResolveEventLoading, setIsResolveEventLoading] = useState(false);
  const [incidentList, setIncidentList] = useState([]);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isLoading, setLoader] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const [isDataFiltered, setIsDataFiltered] = useState(false);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const cameraList = useSelector((state) => state?.deviceReducer?.cameraList);
  const [isResolveCheckoxSelected, setIsResolveCheckboxSelected] = useState(false);
  const [startDate, setStartDate] = useState(filterOptions[DATE_TIME.START_TIME] ? getFormattedDate(filterOptions[DATE_TIME.START_TIME]) : "");
  const [endDate, setEndDate] = useState(filterOptions[DATE_TIME.END_TIME] ? getFormattedDate(filterOptions[DATE_TIME.END_TIME]) : "");
  const [totalIncidents, setTotalIncidents] = useState(0);
  const [unresolvedIncidentsCount, setUnresolvedIncidentsCount] = useState(0);
  const [isUnresolvedCountLoading, setIsUnresolvedCountLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [cvApps, setCvApps] = useState(null);
  const [companionid, setCompanionid] = useState(filterOptions[FILTERS.COMPANION]?.length ? filterOptions[FILTERS.COMPANION] : "");
  const [siteid, setSiteid] = useState(filterOptions[FILTERS.LOCATION]?.siteid || "");
  const [floorid, setFloorid] = useState(filterOptions[FILTERS.LOCATION]?.floorid || "");
  const [areaid, setAreaid] = useState(filterOptions[FILTERS.LOCATION]?.areaid || "");
  const [eventType, setEventType] = useState(filterOptions[FILTERS.EVENTS]?.length ? filterOptions[FILTERS.EVENTS] : "");
  const [severity, setSeverity] = useState(filterOptions[FILTERS.SEVERITY]?.length ? filterOptions[FILTERS.SEVERITY] : "");
  const [status, setStatus] = useState(filterOptions[FILTERS.STATUS]?.length ? filterOptions[FILTERS.STATUS] : "");
  const [startTime, setStartTime] = useState(filterOptions[DATE_TIME.START_TIME] ? filterOptions[DATE_TIME.START_TIME] : "");
  const [endTime, setEndTime] = useState(filterOptions[DATE_TIME.END_TIME] ? filterOptions[DATE_TIME.END_TIME] : "");
  const [order, setOrder] = useState("recent");

  const dispatch = useDispatch();

  const cvAppListFromReducer = useSelector((state) => state.cvAppReducer.cvAppList);
  // Deduplicate by display name — prevents v1/v2 variants of same app showing twice.
  // Memoized to keep a stable reference; plain .filter() in component body creates a
  // new array every render, causing the useEffect([cvAppList]) loop.
  const cvAppList = useMemo(() => {
    const seenNames = new Set();
    return (cvAppListFromReducer ?? []).filter((app) => {
      // 1. Extract the unique identifier once
      const appName = app?.resources?.displayNameFull || app?.resources?.displayName || app?.name;

      // 2. If we've already processed this name, skip it
      if (seenNames.has(appName)) {
        return false;
      }

      // 3. Otherwise, mark it as seen and keep it in the array
      seenNames.add(appName);
      return true;
    });
  }, [cvAppListFromReducer]);
  const companionDeviceList = useSelector((state) => state.deviceReducer.companionDeviceList);
  const sites = siteTagList?.sites;

  const filterCategories = useMemo(() => {
    return [FILTERS.EVENTS, FILTERS.LOCATION, FILTERS.COMPANION, FILTERS.SEVERITY, FILTERS.STATUS];
  }, []);

  const threeYearsAgo = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 3);
    return d;
  }, []);

  const emptyFilters = filterCategories.reduce((acc, category) => {
    if (category === FILTERS.LOCATION) {
      acc[category] = { siteid: "", floorid: "", areaid: "" };
    } else {
      acc[category] = [];
    }
    return acc;
  }, {});
  const [selectedFilterOptions, setSelectedFilterOptions] = useState(filterOptions || emptyFilters);
  const [selectedOptions, setSelectedOptions] = useState(filterOptions || emptyFilters);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    [FILTERS.EVENTS, FILTERS.COMPANION, FILTERS.SEVERITY, FILTERS.STATUS].forEach((key) => {
      if (Array.isArray(selectedFilterOptions[key])) {
        count += selectedFilterOptions[key].length;
      }
    });
    if (selectedFilterOptions[FILTERS.LOCATION]?.siteid) count += 1;
    return count;
  }, [selectedFilterOptions]);

  const isOnlyUnresolvedFilter = useMemo(
    () => Array.isArray(status) && status.length === 1 && status[0] === EVENT_STATUS.UNRESOLVED,
    [status]
  );
  // useMemo (not useState) so filters recompute when Redux data loads after mount.
  // useState with a deps array silently ignores the array — only useMemo respects it.
  const filters = useMemo(
    () => ({
      Events:
        cvAppList && cvAppList.length
          ? cvAppList
              .filter((app) => app.name?.trim() !== "" && app.image?.trim() !== "" && app.image !== "ObjectBlurring")
              .map((app, index) => ({
                name: app?.resources?.displayNameFull || app?.resources?.displayName || app?.name,
                key: `${app.id}_${index}`,
                value: app.image
              }))
          : [],
      Companion:
        companionDeviceList && Array.isArray(companionDeviceList) && companionDeviceList.length > 0
          ? companionDeviceList.filter((list) => list?.id && list?.name).map((app, index) => ({
            name: app?.name,
            key: `${app?.id}_${index}`,
            value: app?.id
          }))
          : [],
      Severity: [
        {
          key: "critical_1",
          name: SEVERITY_LEVELS.CRITICAL,
          value: SEVERITY_LEVELS.CRITICAL
        },
        {
          key: "minor_2",
          name: SEVERITY_LEVELS.MINOR,
          value: SEVERITY_LEVELS.MINOR
        }
      ],
      Status: [
        {
          key: "unresolved_1",
          name: EVENT_STATUS.UNRESOLVED,
          value: EVENT_STATUS.UNRESOLVED
        },
        {
          key: "resolved_2",
          name: EVENT_STATUS.RESOLVED,
          value: EVENT_STATUS.RESOLVED
        }
      ],
      Location: fetchLocationData(cameraList).map((location, index) => ({
        name: `${location.site.name} - ${location.floor.name} - ${location.area.name}`,
        key: `location_${index}`,
        value: `site_${location.site.id}`
      }))
    }),
    [sites, cvAppList, companionDeviceList, cameraList]
  );

  const handleFilterChange = (tab, option, type = "") => {
    let options = {};
    setSelectedOptions((prevSelections) => {
      if (tab === FILTERS.LOCATION) {
        // Handle Location filter specially
        if (option.value === "clear_location") {
          // Clear all Location data
          options = {
            ...prevSelections,
            [tab]: { siteid: "", floorid: "", areaid: "" }
          };
        } else {
          const value = option.value;
          let locationData = { ...prevSelections.Location } || {};
          if (value.startsWith("site_")) {
            const siteid = value.replace("site_", "");
            locationData = {
              siteid: siteid,
              floorid: "", // Clear floor and area when new site is selected
              areaid: ""
            };
          } else if (value.startsWith("floor_")) {
            const floorid = value.replace("floor_", "");
            locationData = {
              ...locationData,
              floorid: floorid,
              areaid: "" // Clear area when new floor is selected
            };
          } else if (value.startsWith("area_")) {
            const areaid = value.replace("area_", "");
            locationData = {
              ...locationData,
              areaid: areaid
            };
          }
          options = {
            ...prevSelections,
            [tab]: locationData
          };
        }
      } else {
        // Handle other filters normally
        const currentSelections = Array.isArray(prevSelections[tab]) ? prevSelections[tab] : [];
        const isSelected = currentSelections.includes(option.value);
        options = {
          ...prevSelections,
          [tab]: isSelected
            ? currentSelections.filter((item) => item !== option.value) // Uncheck
            : [...currentSelections, option.value] // Check
        };
      }
      return options;
    });
    if (type === "filter-spread") {
      handleApplyFilter("", options);
    }
  };

  const handleApplyFilter = (e, options = null) => {
    const filterData = options ?? selectedOptions;
    setSelectedFilterOptions(filterData);
    setShowFiltersModal(false);

    const payload = {
      ...filterData,
      startTime,
      endTime
    };
    dispatch(eventSettings(payload));

    /**
     * Helper function to safely read an array filter, and update the state.
     * Sets the state to the array if it has elements, or to an empty string ("") if empty.
     * @param {Function} setter The local state setter (e.g., setSeverity)
     * @param {string} key The key from the FILTERS enum (e.g., FILTERS.SEVERITY)
     */
    const updateFilterState = (setter, key) => {
      const value = filterData[key];
      if (Array.isArray(value)) {
        setter(value.length === 0 ? "" : value);
      }
    };

    // Update all array-based filters using the helper function
    updateFilterState(setSeverity, FILTERS.SEVERITY);
    updateFilterState(setEventType, FILTERS.EVENTS);
    updateFilterState(setCompanionid, FILTERS.COMPANION);
    updateFilterState(setStatus, FILTERS.STATUS);

    // Handle the object-based Location filter
    const locationData = filterData[FILTERS.LOCATION];
    if (locationData) {
      setSiteid(locationData.siteid || "");
      setFloorid(locationData.floorid || "");
      setAreaid(locationData.areaid || "");
    }
    setIsDataFiltered(true);
  };

  const handleCancelFilter = () => {
    setSelectedOptions(selectedFilterOptions);
    setShowFiltersModal(false);
  };

  const handleResetFilter = () => {
    setSelectedOptions(emptyFilters);
    setSelectedFilterOptions(emptyFilters);

    const payload = {
      ...emptyFilters,
      startTime: "",
      endTime: ""
    };
    dispatch(eventSettings(payload));

    setSeverity("");
    setEventType("");
    setCompanionid("");
    setStatus("");

    setSiteid("");
    setFloorid("");
    setAreaid("");
  };

  useEffect(() => {
    setCvApps(cvAppList);
  }, [cvAppList]);

  useEffect(() => {
    dispatch(fetchCVApp());
  }, []);

  // Check resolution status when activeSession becomes available
  useEffect(() => {
    if (activeSession?.accountId) {
      dispatch(checkResolveStatusRequest(activeSession.accountId));
    }

    // Stop polling when user navigates away from events page
    return () => {
      dispatch(stopResolveStatusPolling());
    };
  }, [activeSession?.accountId, dispatch]);

  // Show notification based on resolution status
  useEffect(() => {
    let isMounted = true;

    if (resolveStatus?.status === RESOLVE_STATUS.IN_PROGRESS) {
      const notificationInfo = {
        type: EVENTS_INFO_CONSTANTS.NOTIFICATION_TYPE_INFO,
        title: EVENTS_INFO_CONSTANTS.RESOLUTION_IN_PROGRESS_TITLE,
        subtitle: EVENTS_INFO_CONSTANTS.RESOLUTION_IN_PROGRESS_SUBTITLE,
        severity: "info",
        showNotification: true,
        autoDismiss: false,
        isDismissible: true
      };
      if (isMounted) {
        dispatch(showNotificationBanner(notificationInfo));
      }
    } else if (resolveStatus?.status === RESOLVE_STATUS.COMPLETE) {
      const notificationInfo = {
        type: EVENTS_INFO_CONSTANTS.NOTIFICATION_TYPE_EVENTS_RESOLVED,
        title: EVENTS_INFO_CONSTANTS.EVENTS_RESOLVED_TITLE,
        subtitle: EVENTS_INFO_CONSTANTS.EVENTS_RESOLVED_SUCCESS,
        showNotification: true,
        priority: "success"
      };
      if (isMounted) {
        dispatch(showNotificationBanner(notificationInfo));
        callfilterEventApi(reducerPageNumber);
      }
    } else if (resolveStatus?.status === RESOLVE_STATUS.IDLE) {
      if (isMounted) {
        dispatch(showNotificationBanner({ showNotification: false }));
      }
    }

    return () => {
      isMounted = false;
    };
  }, [resolveStatus?.status, dispatch]);

  useEffect(() => {
    if (location.state?.fromCriticalCount) {
      const criticalFilters = {
        ...emptyFilters,
        [FILTERS.SEVERITY]: [SEVERITY_LEVELS.CRITICAL],
        [FILTERS.STATUS]: [EVENT_STATUS.UNRESOLVED]
      };

      setSelectedFilterOptions(criticalFilters);
      setSelectedOptions(criticalFilters);
      setSeverity([SEVERITY_LEVELS.CRITICAL]);
      setStatus([EVENT_STATUS.UNRESOLVED]);

      dispatch(eventSettings(criticalFilters));
      setIsDataFiltered(true);
    }
  }, [location.state]);


  const handleSelect = () => {
    const endOfDayDate = endOfDay(new Date(endDate));

    setStartTime(new Date(startDate));
    setEndTime(endOfDayDate);
    dispatch(
      eventSettings({
        ...filterOptions,
        startTime: new Date(startDate),
        endTime: endOfDayDate
      })
    );
    setIncidentList(incidentList);

    // Reset page number to 1 when date filters are applied
    dispatch(eventPageNumber(1));
  };

  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);
    if (endDate && newStartDate && new Date(endDate) < new Date(newStartDate)) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (endDate) => {
    setEndDate(endDate);
  };

  useEffect(() => {
    // Skip if it's the initial mount - dates are being restored from Redux
    if (isInitialMount.current) {
      return;
    }
    
    if (startDate && endDate) {
      setIsDataFiltered(true);
      handleSelect();
    }
  }, [startDate, endDate]);

  const handlePageClick = (page) => {
    setIsResolveCheckboxSelected(false);
    dispatch(eventPageNumber(page));
    callfilterEventApi(page);
    setApiLoading(true);
  };

  const clearFilters = () => {
    const payload = {
      ...emptyFilters,
      startTime: "",
      endTime: ""
    };
    dispatch(eventSettings(payload));
    setStatus("");
    setSiteid("");
    setFloorid("");
    setAreaid("");
    setCompanionid("");
    setEventType("");
    setSeverity("");
    setIsDataFiltered(false);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setOrder("recent");
  };

  // Helper function to create incidents payload
  const createIncidentsPayload = (overrides = {}) => {
    return {
      accountname: activeSession.accountId,
      filter: {
        selection: {
          eventtype: eventType || "",
          starttime: startTime || "",
          endtime: endTime || "",
          status: status || "",
          siteid: siteid ? [siteid] : "",
          floorid: floorid ? [floorid] : "",
          areaid: areaid ? (Array.isArray(areaid) ? areaid : [areaid]) : "",
          companionid: companionid || "",
          severity: severity || "",
          ...overrides.selection
        },
        sort: [
          {
            order: order ? order : ""
          }
        ],
        limitnumber: 10,
        page: "",
        pagenumber: 1,
        ...overrides.filter
      }
    };
  };

  const callfilterEventApi = async (selectedPage) => {
    const date = new Date();
    const timeZone = moment.tz.guess();
    const formattedDate = moment(date).format("MMM D,");
    const formattedTime = moment(date).tz(timeZone).format("h:mm:ss A z");
    dispatch(addLastUpdatedTime(`${formattedDate} ${formattedTime}`));
    try {
      setApiLoading(true);
      setIncidentList([]);
      setApiError(false);
      const payload = createIncidentsPayload({
        filter: { pagenumber: selectedPage || 1 }
      });
      const resp = await getIncidentsData(payload);
      const fetchIncidentsRsp = resp?.res;
      if (!isNaN(parseInt(resp.headers.XTotalCount))) {
        setTotalIncidents(parseInt(parseInt(resp.headers.XTotalCount)));
      }
      if (Array.isArray(fetchIncidentsRsp) && fetchIncidentsRsp?.length > 0) {
        setIncidentList(fetchIncidentsRsp);
      } else if (fetchIncidentsRsp?.length === 0) {
        setIncidentList([]);
      } else {
        setIncidentList([]);
      }

      // Reset stale unresolved count — will be refetched lazily on "Select All" click
      setUnresolvedIncidentsCount(0);
    } catch (e) {
      console.log(e);
      setApiError(true);
    } finally {
      setApiLoading(false);
      setLoader(false);
    }
  };

  const fetchUnresolvedCount = async () => {
    if (isOnlyUnresolvedFilter) {
      // Already filtered to unresolved only — reuse the existing total (no extra call)
      setUnresolvedIncidentsCount(totalIncidents);
      return;
    }
    try {
      setIsUnresolvedCountLoading(true);
      const unresolvedPayload = createIncidentsPayload({
        selection: { status: [EVENT_STATUS.UNRESOLVED] },
        filter: { limitnumber: 1, pagenumber: 1 }
      });
      const unresolvedResp = await getIncidentsData(unresolvedPayload);
      if (!isNaN(parseInt(unresolvedResp.headers.XTotalCount))) {
        setUnresolvedIncidentsCount(parseInt(unresolvedResp.headers.XTotalCount));
      }
    } catch (error) {
      console.log("Error fetching unresolved count:", error);
      setUnresolvedIncidentsCount(0);
    } finally {
      setIsUnresolvedCountLoading(false);
    }
  };

  const handleBulkResolve = async (notes = "", type = "selectedids") => {
    const selectedIds = selectedIncidentList.map((item) => item?.id);
    try {
      setIsResolveEventLoading(true);
      const response = await resolveBulkEvents(
        {
          ...(type === "selectedids" && { eventids: [...selectedIds] }),
          customdata: {
            comment: notes
          },
          ...(type !== "selectedids" && {
            filter: {
              $selection: {
                eventType: eventType || [],
                startTime: startTime || "",
                endTime: endTime || "",
                status: EVENT_STATUS.UNRESOLVED,
                siteId: siteid ? [siteid] : "",
                floorId: floorid ? [floorid] : "",
                areaId: areaid ? (Array.isArray(areaid) ? areaid : [areaid]) : "",
                companionId: companionid || "",
                severity: severity || []
              }
            }
          })
        },
        activeSession
      );
      
      if (response?.status === 200 || response?.status === 207) {
        const notificationInfo = {
          type: EVENTS_INFO_CONSTANTS.NOTIFICATION_TYPE_EVENTS_RESOLVED,
          title: EVENTS_INFO_CONSTANTS.EVENTS_RESOLVED_TITLE,
          subtitle: response?.data?.failureCount === 0
            ? EVENTS_INFO_CONSTANTS.EVENTS_RESOLVED_SUCCESS
            : EVENTS_INFO_CONSTANTS.EVENTS_RESOLVED_PARTIAL,
          showNotification: true,
          priority: "success"
        };
        dispatch(showNotificationBanner(notificationInfo));
        // Sync resolution: events resolved immediately, refresh list now
        callfilterEventApi(reducerPageNumber);
      } else {
        const notificationInfo = {
          type: EVENTS_INFO_CONSTANTS.NOTIFICATION_TYPE_EVENTS_RESOLVED,
          title: EVENTS_INFO_CONSTANTS.EVENTS_BEING_RESOLVED_TITLE,
          subtitle: EVENTS_INFO_CONSTANTS.EVENTS_BEING_RESOLVED_SUBTITLE,
          showNotification: true,
          priority: "info"
        };
        dispatch(showNotificationBanner(notificationInfo));
        // Async resolution: start polling — refresh happens when COMPLETE detected
        dispatch(checkResolveStatusRequest(activeSession?.accountId, true));
      }
    } catch (error) {
      console.log("error resolving bulk events: ", error);
      const notificationInfo = {
        type: EVENTS_INFO_CONSTANTS.NOTIFICATION_TYPE_EVENTS_RESOLVED,
        title: EVENTS_INFO_CONSTANTS.EVENTS_NOT_RESOLVED_TITLE,
        subtitle: EVENTS_INFO_CONSTANTS.EVENTS_NOT_RESOLVED_SUBTITLE,
        showNotification: true,
        priority: "critical"
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      setShowResolveEvents({ isOpen: false });
      setIsResolveCheckboxSelected(false);
      setIsResolveEventLoading(false);
      dispatch(removeBulkSelectedIncidents());
      dispatch(eventPageNumber(reducerPageNumber));
    }
  };

  useEffect(() => {
    if (!filterOptions) return;

    // Helper function to handle array-based filters
    const updateArrayState = (key, setter) => {
      const value = filterOptions[key];
      if (Array.isArray(value) && value.length > 0) {
        setter(value);
      }
    };

    setSelectedFilterOptions(filterOptions);
    setSelectedOptions(filterOptions);

    // Array-based filters
    updateArrayState(FILTERS.SEVERITY, setSeverity);
    updateArrayState(FILTERS.EVENTS, setEventType);
    updateArrayState(FILTERS.COMPANION, setCompanionid);
    updateArrayState(FILTERS.STATUS, setStatus);

    // Location filter (object-based)
    const locationData = filterOptions[FILTERS.LOCATION];
    if (locationData) {
      setSiteid(locationData.siteid || "");
      setFloorid(locationData.floorid || "");
      setAreaid(locationData.areaid || "");
    }

    // Date/Time filters
    if (filterOptions[DATE_TIME.START_TIME]) {
      setStartDate(getFormattedDate(filterOptions[DATE_TIME.START_TIME]));
    }
    if (filterOptions[DATE_TIME.END_TIME]) {
      setEndDate(getFormattedDate(filterOptions[DATE_TIME.END_TIME]));
    }
  }, [filterOptions]);

  useEffect(() => {
    if (selectedIncidentList && Array.isArray(selectedIncidentList) && selectedIncidentList.length === 0) {
      setIsResolveCheckboxSelected(false);
    }
  }, [selectedIncidentList]);

  useEffect(() => {
    // On initial mount, preserve the page number from Redux
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (Number.isFinite(reducerPageNumber) && reducerPageNumber !== 1) {
        callfilterEventApi(reducerPageNumber);
      } else {
        callfilterEventApi(reducerPageNumber || 1);
      }
      return;
    }

    // On filter changes (not initial mount), reset to page 1
    dispatch(eventPageNumber(1));
    callfilterEventApi(1);
  }, [startTime, endTime, eventType, status, siteid, companionid, severity, floorid, areaid, order]);


  // ✅ Correct
  const isDisableAllSelectCheckbox = useMemo(() => incidentList?.some((m) => m.status === EVENT_STATUS.UNRESOLVED), [incidentList]);

  // Keeping it intentially, commented for future use
  // const clearDate = () => {
  //   setStartDate(null);
  //   setEndDate(null);
  //   setDateSelection(false);
  //   // setSelectionRange([
  //   //   {
  //   //     startDate: new Date(),
  //   //     endDate: new Date(),
  //   //     key: "selection"
  //   //   }
  //   // ]);
  //   setStartTime("");
  //   setEndTime("");
  //   dispatch(eventSettings({ ...filterOptions, startTime: "", endTime: "" }));
  //   // setIncidentList([...incidentList]);
  // };

  return (
    <>
      {isLoading ? (
        <Box className="incidents-loader-wrapper">
          <Loader active={isLoading} fullscreen={false} surface="light" />
        </Box>
      ) : (
        <>
          <GettingStartedModal />
          <div className="incidents-breadcrumbs-row">
            <Breadcrumbs surface="light"></Breadcrumbs>
          </div>
          <Title size="large">Events</Title>
          <br />

          <FilterSpreadContainer
            callfilterEventApi={callfilterEventApi}
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleApplyFilter={handleApplyFilter}
          />
          <br />

          {/* Keeping it intentially, commented for future use */}
          {/* {(startDate || endDate) ? (
            <div style={{ textAlign: "right" }}>
              <TextLink
              surface="light"
              size="small"
              data-testid="resetFilterButton"
              onClick={() => {
                clearDate();
              }}
              >
                Clear Date Filter
              </TextLink>
            </div>
          ) : null} */}

          <Box className="incidents-toolbar">
            <div className="incidents-left-actions">
              {incidentList.length > 0 && (
                <>
                  <span className="events_select_all_wrapper">
                    <Checkbox
                      className="events_select_all"
                      width="auto"
                      disabled={!isDisableAllSelectCheckbox}
                      error={false}
                      selected={isResolveCheckoxSelected}
                      onChange={(e, { selected }) => {
                        setIsResolveCheckboxSelected(selected);
                        if (selected) {
                          dispatch(addBulkSelectedIncidents(incidentsList));
                          fetchUnresolvedCount();
                        } else {
                          dispatch(removeBulkSelectedIncidents());
                        }
                      }}
                    />
                  </span>
                  {!isDisableAllSelectCheckbox && (
                    <span className="unresolved-event-text">
                      0 Unresolved event available to resolve.
                    </span>
                  )}
                </>
              )}
              {selectedIncidentList && Array.isArray(selectedIncidentList) && selectedIncidentList.length > 0 && (
                <Button
                  size="small"
                  use="secondary"
                  disabled={isResolveEventLoading || resolveStatus?.status === RESOLVE_STATUS.IN_PROGRESS}
                  onClick={() => {
                    setShowResolveEvents({ isOpen: true });
                    setActionType("selectedids");
                  }}
                  data-testid={INCIDENTS_TEST_IDS.INCIDENTS_RESOLVE_BUTTON}
                >
                  {resolveStatus?.status === RESOLVE_STATUS.IN_PROGRESS
                    ? EVENTS_INFO_CONSTANTS.RESOLVING_TEXT
                    : isResolveEventLoading
                      ? EVENTS_INFO_CONSTANTS.LOADING_TEXT
                      : "Resolve"}
                </Button>
              )}
            </div>
            <div className="incidents-right-controls">
              <div className="incidents-date-pickers">
                <div className="incidents-datepicker-wrapper">
                  <DatePicker
                    dateFormat="MM/DD/YYYY"
                    data-testid="events_datapickerFromInput"
                    alwaysOpen={false}
                    readOnly={false}
                    disabled={false}
                    surface="light"
                    minDate={threeYearsAgo}
                    label="From"
                    onChange={handleStartDateChange}
                    value={getFormattedDate(startDate)}
                    maxDate={endDate ? new Date(endDate) : new Date()}
                    required={false}
                    id="events_start_datepicker"
                    inputHeight="32px"
                  />
                  {startDate && (
                    <Tooltip title="Clear date">
                      <IconButton
                        size="small"
                        className="incidents-datepicker-clear-btn"
                        onClick={() => {
                          setStartDate("");
                          setStartTime("");
                          dispatch(eventSettings({ ...filterOptions, startDate: "", startTime: "" }));
                        }}
                      >
                        <CloseIcon className="incidents-datepicker-clear-icon" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <div className="incidents-datepicker-wrapper">
                  <DatePicker
                    dateFormat="MM/DD/YYYY"
                    data-testid="events_datapickerToInput"
                    alwaysOpen={false}
                    readOnly={false}
                    disabled={false}
                    surface="light"
                    minDate={startDate ? new Date(startDate) : new Date(1988, 1, 1)}
                    maxDate={new Date()}
                    label="To"
                    onChange={handleEndDateChange}
                    value={getFormattedDate(endDate)}
                    required={false}
                    id="events_end_datepicker"
                    inputHeight="32px"
                  />
                  {endDate && (
                    <Tooltip title="Clear date">
                      <IconButton
                        size="small"
                        className="incidents-datepicker-clear-btn"
                        onClick={() => {
                          setEndDate("");
                          setEndTime("");
                          dispatch(eventSettings({ ...filterOptions, endDate: "", endTime: "" }));
                        }}
                      >
                        <CloseIcon className="incidents-datepicker-clear-icon" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>

              <div className="incidents-filter-icon-wrapper">
                <div className="incidents-filter-dropdown-container">
                  <Tooltip title="Filter events">
                    <Badge
                      badgeContent={appliedFilterCount}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        '& .MuiBadge-badge': {
                          backgroundColor: 'black',
                          color: 'white'
                        }
                      }}
                    >
                      <IconButton
                        className="events_filter_icon_button"
                        data-testid={INCIDENTS_TEST_IDS.INCIDENTS_FILTER_ICON_BUTTON}
                        onClick={() => {
                          setShowFiltersModal(!showFiltersModal);
                        }}
                      >
                        <Icon name="filter" size="large" />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                  {showFiltersModal && filters && Object.keys(filters)?.length && (
                    <FilterComponentUI
                      filters={filters}
                      filterCategories={filterCategories}
                      selectedOptions={selectedOptions}
                      handleFilterChange={handleFilterChange}
                      handleApply={handleApplyFilter}
                      handleCancel={handleCancelFilter}
                      handleReset={handleResetFilter}
                      pageIdentifier="Events"
                    />
                  )}
                </div>
              </div>
            </div>
          </Box>

          {selectedIncidentList && Array.isArray(selectedIncidentList) && selectedIncidentList.length > 0 && (
            <SelectAllEventsBanner
              selectedIncidentList={selectedIncidentList}
              setActionType={setActionType}
              setShowResolveEvents={setShowResolveEvents}
              unresolvedCount={unresolvedIncidentsCount}
              resolveStatus={resolveStatus}
              isUnresolvedCountLoading={isUnresolvedCountLoading}
            />
          )}

          <EventTable
            incidentList={incidentList}
            clearFilters={clearFilters}
            handlePageClick={handlePageClick}
            totalIncidentCount={totalIncidents}
            isDataFiltered={isDataFiltered}
            itemsPerPage={itemsPerPage}
            apiLoading={apiLoading}
            selectedPage={reducerPageNumber}
            scrollPositionRef={scrollPositionRef}
            apiError={apiError}
            callfilterEventApi={callfilterEventApi}
            page={reducerPageNumber}
          />
          <BulkResolveEventsModal
            setShowResolveEvents={setShowResolveEvents}
            showResolveEvents={showResolveEvents}
            handleResolveIncident={handleBulkResolve}
            loader={isResolveEventLoading}
            actionType={actionType}
            unresolvedCount={unresolvedIncidentsCount}
            selectedCount={selectedIncidentList?.length || 0}
          />
        </>
      )}
    </>
  );
};

export default Incidents;
